import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getChargeBySlug, resolveApplicableEntities } from '../../services/dataService';
import { Charge } from '../types';
import LoadingSpinner from '../../components/LoadingSpinner';
import Breadcrumbs from '../../components/Breadcrumbs';
import ShareButton from '../../components/ShareButton';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import AdComponent from '../../components/AdComponent';

const InfoSection: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
    <div className="bg-white p-5 sm:p-8 rounded-xl shadow-sm border border-gray-200 transition-shadow hover:shadow-md">
        <div className="flex items-center mb-4 text-secondary">
            <span className="mr-3 text-primary bg-blue-50 p-2 rounded-lg flex-shrink-0">{icon}</span>
            <h2 className="text-lg sm:text-xl font-bold font-poppins">{title}</h2>
        </div>
        <div className="prose prose-sm max-w-none text-neutral-light leading-relaxed">
            {children}
        </div>
    </div>
);

const ChargeDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [charge, setCharge] = useState<Charge | null>(null);
    const [loading, setLoading] = useState(true);
    const [addedItems, setAddedItems] = useState<Set<string>>(() => {
        const items = localStorage.getItem('comparisonItems')?.split(',').filter(Boolean) || [];
        return new Set(items);
    });

    useEffect(() => {
        if (slug) {
            setLoading(true);
            getChargeBySlug(slug).then(data => {
                setCharge(data || null);
                setLoading(false);
            });
        }
    }, [slug]);

    const applicableEntities = useMemo(() => {
        if (!charge) return [];
        return resolveApplicableEntities(charge);
    }, [charge]);

    const handleAddToCompare = (entityId: string) => {
        if (!charge) return;
        const comparisonId = `${charge.id}-${entityId}`;

        // Read current items from localStorage
        const currentItems = localStorage.getItem('comparisonItems')?.split(',').filter(Boolean) || [];
        const newItems = new Set([...currentItems, comparisonId]);

        // Update localStorage and component state
        localStorage.setItem('comparisonItems', Array.from(newItems).join(','));
        setAddedItems(newItems);
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!charge) {
        return (
            <div className="text-center py-20 bg-background">
                <h2 className="text-2xl font-bold text-secondary">Charge not found</h2>
                <Link to="/charges" className="text-primary hover:underline mt-4 inline-block">
                    &larr; Back to all charges
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-background py-8 sm:py-12">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
                <Breadcrumbs />
                 <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-8 sm:mb-10 gap-4">
                    <div className="flex-grow">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                             <span className="bg-blue-100 text-primary font-bold px-3 py-1 rounded-md text-xs sm:text-sm font-roboto-mono tracking-wide">{charge.code}</span>
                             <span className="text-gray-500 text-xs sm:text-sm font-medium uppercase tracking-wide bg-gray-100 px-2 py-1 rounded-md">{charge.category}</span>
                        </div>
                        <h1 className="text-2xl sm:text-4xl font-extrabold font-poppins tracking-tight text-secondary leading-tight">{charge.fullName}</h1>
                        <p className="text-base sm:text-lg text-neutral-light font-medium mt-2">Known as: <span className="text-gray-900">{charge.name}</span></p>
                    </div>
                     <div className="flex-shrink-0 w-full sm:w-auto">
                        <ShareButton
                            title={`What is a '${charge.name}' charge? | ChargesDecoder`}
                            text={`I found a clear explanation for the '${charge.name}' (${charge.fullName}) charge on ChargesDecoder.`}
                            className="w-full sm:w-auto"
                            customClasses="flex justify-center items-center gap-2 px-4 py-3 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-100 w-full sm:w-auto transition-colors"
                        />
                     </div>
                </header>

                <main className="space-y-6 sm:space-y-8">
                    <InfoSection
                        title="What Does This Mean?"
                        icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    >
                        <MarkdownRenderer content={charge.whatItMeans} />
                    </InfoSection>

                    <InfoSection
                        title="Why Was This Charged?"
                        icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
                    >
                        <MarkdownRenderer content={charge.whyCharged} />
                    </InfoSection>

                    <AdComponent />

                    <InfoSection
                        title="How To Avoid It"
                        icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>}
                    >
                        <ol className="list-decimal list-inside space-y-3 text-gray-700">
                            {charge.howToAvoid.map((tip, index) => (
                                <li key={index} className="pl-2 marker:font-semibold marker:text-primary">{tip}</li>
                            ))}
                        </ol>
                    </InfoSection>

                    {applicableEntities.length > 0 && (
                        <InfoSection
                            title="Compare Fees Across Banks/Apps"
                            icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
                        >
                             <ul className="space-y-4">
                                {applicableEntities.map(entity => {
                                    const comparisonId = `${charge.id}-${entity.id}`;
                                    const isAdded = addedItems.has(comparisonId);
                                    return (
                                        <li key={entity.id} className="flex flex-col sm:flex-row justify-between sm:items-center bg-gray-50 p-4 sm:p-5 rounded-xl border border-gray-100 gap-4 transition-all duration-200 hover:bg-white hover:shadow-md hover:border-blue-100">
                                            <div className="flex-grow">
                                                <span className="font-bold text-secondary text-base sm:text-lg block leading-tight mb-2 sm:mb-1">{entity.name}</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Fee:</span>
                                                    <span className="text-base font-bold text-primary-dark font-roboto-mono">{entity.chargeDetails.amount}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleAddToCompare(entity.id)}
                                                disabled={isAdded}
                                                className={`text-sm font-bold px-6 py-3 sm:py-2 rounded-lg transition-all w-full sm:w-auto flex-shrink-0 shadow-sm flex items-center justify-center gap-2 ${
                                                    isAdded
                                                        ? 'bg-emerald-50 text-emerald-600 cursor-default border border-emerald-100'
                                                        : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary hover:text-white'
                                                }`}
                                            >
                                                {isAdded ? (
                                                    <>
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                                        Added
                                                    </>
                                                ) : 'Compare +'}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                            {addedItems.size > 0 && (
                                <div className="mt-8 text-center sticky bottom-4 z-10 sm:static">
                                    <Link to="/compare" className="inline-flex items-center justify-center font-bold text-white bg-primary hover:bg-primary-dark w-full sm:w-auto px-8 py-3 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                                        View Comparison ({addedItems.size}) <span className="ml-2">&rarr;</span>
                                    </Link>
                                </div>
                            )}
                        </InfoSection>
                    )}
                </main>
            </div>
        </div>
    );
};

export default ChargeDetailPage;